import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, FileCheck2, Loader2, Wallet } from 'lucide-react';
import { fetchLicences, fetchSaisons, urlAttestation } from '../services/api';
import { eurosDepuisCentimes as euros } from '../utils/montants';

const FILTRES_PAIEMENT = [
  { code: '', label: 'Tous' },
  { code: 'impaye', label: 'Rien reçu' },
  { code: 'partiel', label: 'Partiels' },
  { code: 'solde', label: 'Soldés' },
  { code: 'retard', label: 'En retard' },
];

const TONS_PAIEMENT: Record<string, string> = {
  solde: 'bg-green-100 text-green-800',
  partiel: 'bg-amber-100 text-amber-800',
  impaye: 'bg-gray-100 text-gray-600',
};

const LIBELLE_PAIEMENT: Record<string, string> = {
  solde: 'Soldée',
  partiel: 'Partielle',
  impaye: 'Rien reçu',
};

/**
 * L'écran du trésorier : qui doit encore, combien, et depuis quand.
 *
 * La question qu'on vient poser ici n'est pas « qui est licencié » — c'est le
 * répertoire qui y répond — mais « où en est la caisse ». Les trois chiffres
 * du haut y répondent avant même qu'on lise une ligne.
 */
export default function TableauCotisations() {
  const [saison, setSaison] = useState<string>('');
  const [paiement, setPaiement] = useState('');
  // Le trésorier relance par catégorie : c'est l'entraîneur des U13 qu'il
  // appelle pour ses U13, pas le club entier.
  const [categorie, setCategorie] = useState('');

  /*
    ET PAR DISCIPLINE, DEPUIS QU'IL PEUT Y EN AVOIR PLUSIEURS.

    Dans une association omnisports, le total du club ne dit rien à personne :
    le responsable de la section judo veut savoir où en est le judo, et le
    bureau veut arbitrer entre les sections. Un chiffre unique mélange les deux
    et n'aide ni l'un ni l'autre.
  */
  const [discipline, setDiscipline] = useState('');

  const saisonsQuery = useQuery({ queryKey: ['saisons'], queryFn: fetchSaisons });
  const saisons: any[] = saisonsQuery.data?.saisons ?? [];

  const query = useQuery({
    queryKey: ['licences', saison, paiement],
    queryFn: () => fetchLicences({ saison, paiement }),
  });

  if (query.isPending) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  const toutes: any[] = query.data?.licences ?? [];
  // Les catégories réellement présentes, pas celles du référentiel : une
  // liste qui propose des cases vides fait chercher pour rien.
  const categories = Array.from(
    new Set(toutes.map((l) => l.categorie).filter(Boolean))
  ).sort() as string[];
  /*
    Les disciplines réellement présentes, pas celles du référentiel.

    Une liste qui propose « Gymnastique » quand aucune gymnaste n'est licenciée
    fait chercher pour rien, et laisse croire à un filtre cassé.
  */
  const disciplines = Array.from(
    new Set(toutes.map((l) => l.discipline).filter(Boolean))
  ).sort() as string[];

  const licences = toutes.filter(
    (l) =>
      (categorie === '' || l.categorie === categorie) &&
      (discipline === '' || l.discipline === discipline),
  );

  // La caisse suit les filtres : afficher le total du club sous une liste
  // de douze joueurs ne dirait rien de ce qu'on regarde.
  const caisse =
    categorie === '' && discipline === ''
      ? query.data?.caisse ?? { du: 0, encaisse: 0, reste: 0, enRetard: 0 }
      : {
          du: licences.reduce((t, l) => t + l.montant, 0),
          encaisse: licences.reduce((t, l) => t + l.regle, 0),
          reste: licences.reduce((t, l) => t + l.reste, 0),
          enRetard: licences.filter((l) => l.enRetard).length,
        };

  if (saisons.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-dashed border-gray-300 p-10 text-center text-sm text-gray-500">
        <Wallet className="w-8 h-8 mx-auto text-gray-300 mb-3" />
        Aucune saison n'est ouverte. Créez-en une dans Administration → Adhérents, puis inscrivez
        vos adhérents depuis leur fiche.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Carte libelle="Cotisations appelées" valeur={euros(caisse.du)} />
        <Carte libelle="Encaissé" valeur={euros(caisse.encaisse)} ton="vert" />
        <Carte libelle="Reste à percevoir" valeur={euros(caisse.reste)} ton={caisse.reste > 0 ? 'ambre' : 'neutre'} />
        <Carte
          libelle="En retard"
          valeur={String(caisse.enRetard)}
          ton={caisse.enRetard > 0 ? 'rouge' : 'neutre'}
        />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center gap-3">
        <select
          value={saison}
          onChange={(e) => setSaison(e.target.value)}
          className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
        >
          {saisons.map((s) => (
            <option key={s.id} value={s.id}>
              Saison {s.libelle}
              {s.courante ? ' (courante)' : ''}
            </option>
          ))}
        </select>
        <div className="flex flex-wrap gap-1">
          {FILTRES_PAIEMENT.map((f) => (
            <button
              key={f.code}
              onClick={() => setPaiement(f.code)}
              className={`px-3 py-1.5 rounded-lg text-sm ${
                paiement === f.code
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        {/*
          La discipline AVANT la catégorie.

          Dans un club omnisports, on choisit d'abord sa section, puis l'âge :
          « les U13 » ne veut rien dire tant qu'on n'a pas dit de quel sport.
          Le menu n'apparaît qu'à partir de deux — ailleurs il n'y a rien à
          départager.
        */}
        {disciplines.length > 1 && (
          <select
            value={discipline}
            onChange={(e) => setDiscipline(e.target.value)}
            className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
          >
            <option value="">Toutes les disciplines</option>
            {disciplines.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        )}

        {categories.length > 1 && (
          <select
            value={categorie}
            onChange={(e) => setCategorie(e.target.value)}
            className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
          >
            <option value="">Toutes les catégories</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        )}
        <span className="text-xs text-gray-500 ml-auto">
          {licences.length} licence{licences.length > 1 ? 's' : ''}
          {(categorie !== '' || discipline !== '') && ` sur ${toutes.length}`}
        </span>
      </div>

      {licences.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-500">
          Aucune licence ne correspond à ce filtre.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
                <th className="px-4 py-3 font-medium">Adhérent</th>
                <th className="px-4 py-3 font-medium">Discipline</th>
                <th className="px-4 py-3 font-medium">Catégorie</th>
                <th className="px-4 py-3 font-medium">N° licence</th>
                <th className="px-4 py-3 font-medium text-right">Cotisation</th>
                <th className="px-4 py-3 font-medium text-right">Réglé</th>
                <th className="px-4 py-3 font-medium text-right">Reste</th>
                <th className="px-4 py-3 font-medium">État</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {licences.map((l) => (
                <tr key={l.id} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="px-4 py-2.5">
                    {/*
                      Le nom mène à la FICHE COMPLÈTE, et non plus au volet du
                      répertoire.

                      Un trésorier qui clique sur un nom depuis ce tableau veut
                      savoir qui c'est, comment le joindre et ce qu'il doit —
                      pas revenir dans une liste où il faut le retrouver. La
                      fiche répond aux trois d'un coup.
                    */}
                    <Link
                      to={`/adherents/${l.adherentId}`}
                      className="font-medium text-gray-900 hover:text-primary-700"
                    >
                      {l.adherent}
                    </Link>
                    {l.enRetard && (
                      <span className="ml-2 inline-flex items-center gap-1 text-xs text-red-700">
                        <AlertTriangle className="w-3 h-3" />
                        retard
                      </span>
                    )}
                  </td>
                  {/*
                    Deux colonnes, et non deux valeurs dans une.

                    « U13 · Judo » se lisait bien à une discipline près. Avec
                    trois sections, on trie du regard une colonne — et on ne
                    trie pas une colonne dont chaque cellule porte deux choses.
                  */}
                  <td className="px-4 py-2.5 text-gray-600">{l.discipline || '—'}</td>
                  <td className="px-4 py-2.5 text-gray-600">{l.categorie || '—'}</td>
                  <td className="px-4 py-2.5 text-gray-600">
                    {l.numero || <span className="text-gray-300">à saisir</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">{euros(l.montant)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-green-700">{euros(l.regle)}</td>
                  <td
                    className={`px-4 py-2.5 text-right tabular-nums ${
                      l.reste > 0 ? 'text-amber-700 font-medium' : 'text-gray-300'
                    }`}
                  >
                    {euros(l.reste)}
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={`inline-block rounded-full px-2 py-0.5 text-xs ${
                        TONS_PAIEMENT[l.statutPaiement] ?? 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {LIBELLE_PAIEMENT[l.statutPaiement] ?? l.statutPaiement}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {l.regle > 0 && (
                      <a
                        href={urlAttestation(l.id)}
                        target="_blank"
                        rel="noreferrer"
                        title="Attestation de paiement"
                        className="inline-flex p-1.5 rounded text-primary-700 hover:bg-primary-50"
                      >
                        <FileCheck2 className="w-4 h-4" />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Carte({ libelle, valeur, ton = 'neutre' }: { libelle: string; valeur: string; ton?: string }) {
  const couleurs: Record<string, string> = {
    neutre: 'text-gray-900',
    vert: 'text-green-700',
    ambre: 'text-amber-700',
    rouge: 'text-red-700',
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-gray-400">{libelle}</p>
      <p className={`text-xl font-semibold tabular-nums ${couleurs[ton]}`}>{valeur}</p>
    </div>
  );
}
